import React from 'react';
import Icon from '../AppIcon';

const DENSITA = { compatta: 'py-2', normale: 'py-4', spaziosa: 'py-6' };
const ALLINEAMENTO = { left: 'text-left', center: 'text-center', right: 'text-right' };
const GIUSTIFICA = { left: '', center: 'justify-center', right: 'justify-end' };

/**
 * Tabella con paginazione e ordinamento lato server (stile tabelle CCO).
 *
 * colonne: [{ key, label, width, align, noSort, sortKey, render(riga) }]
 * Paginazione: pagina (0-based), perPagina, totale, onPagina(n)
 * Ordinamento: sort { key, direction }, onSort(key)
 */
const DataTable = ({
  colonne,
  righe,
  totale,
  loading = false,
  pagina,
  perPagina,
  onPagina,
  sort,
  onSort,
  densita = 'normale',
  messaggioVuoto = 'Nessun elemento trovato',
  rigaKey = 'id',
  onRigaClick,
  rigaSelezionataId,
  rigaDisattivata,
  altezzaMax = 'max-h-[max(18rem,calc(100dvh-22rem))]',
}) => {
  const padClass = DENSITA[densita] ?? DENSITA.normale;
  const larghezzaMin = colonne.reduce((acc, c) => acc + (c.width || 120), 0);
  const totalePagine = Math.max(1, Math.ceil((totale || 0) / perPagina));
  const haPrec = pagina > 0;
  const haSucc = pagina + 1 < totalePagine;

  return (
    <div className="bg-card border border-border/40 rounded-3xl shadow-xl overflow-hidden flex flex-col min-h-0 min-w-0">
      {/* Area scorrevole: verticale e orizzontale, con altezza massima (dvh = corretta anche su mobile) */}
      <div
        className={`overflow-auto overscroll-x-contain ${altezzaMax}`}
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.35) transparent', WebkitOverflowScrolling: 'touch' }}
      >
        <table className="border-collapse" style={{ minWidth: larghezzaMin, width: '100%' }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted/90 backdrop-blur-sm border-b border-border/50">
              {colonne.map((col, i) => {
                const chiave = col.sortKey || col.key;
                const ordinabile = !col.noSort && !!onSort;
                const attiva = ordinabile && sort?.key === chiave;
                return (
                  <th
                    key={col.key}
                    onClick={() => ordinabile && onSort(chiave)}
                    style={{ width: col.width, minWidth: col.width }}
                    className={`
                      px-4 py-3.5 text-[10px] font-black uppercase tracking-[.18em]
                      transition-colors select-none whitespace-nowrap
                      ${ALLINEAMENTO[col.align] ?? ALLINEAMENTO.left}
                      ${ordinabile ? 'cursor-pointer hover:text-primary' : 'cursor-default'}
                      ${attiva ? 'text-primary' : 'text-muted-foreground/60'}
                      ${i === 0 ? 'pl-5' : ''}
                      ${i === colonne.length - 1 ? 'pr-5' : ''}
                    `}
                  >
                    <div className={`inline-flex items-center gap-1.5 ${GIUSTIFICA[col.align] ?? ''}`}>
                      {col.label}
                      {attiva && (
                        <Icon
                          name={sort.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={10}
                          className="text-primary"
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className={loading && righe.length > 0 ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
            {loading && righe.length === 0 ? (
              <tr>
                <td colSpan={colonne.length} className="py-24 text-center">
                  <div className="inline-flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                    <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">
                      Caricamento...
                    </span>
                  </div>
                </td>
              </tr>
            ) : righe.length === 0 ? (
              <tr>
                <td colSpan={colonne.length} className="py-20 text-center">
                  <div className="inline-flex flex-col items-center gap-3">
                    <Icon name="PackageSearch" size={36} className="text-muted-foreground/20" />
                    <span className="text-sm font-bold text-muted-foreground/40">{messaggioVuoto}</span>
                  </div>
                </td>
              </tr>
            ) : righe.map((riga, idx) => {
              const selezionata = rigaSelezionataId !== undefined && rigaSelezionataId !== null && riga[rigaKey] === rigaSelezionataId;
              const disattivata = rigaDisattivata ? rigaDisattivata(riga) : false;
              const pari = idx % 2 === 0;
              return (
                <tr
                  key={riga[rigaKey] ?? idx}
                  onClick={onRigaClick ? () => onRigaClick(riga) : undefined}
                  className={`
                    group border-b border-border/[0.15] transition-all duration-200
                    ${onRigaClick ? 'cursor-pointer' : 'cursor-default'}
                    ${selezionata
                      ? 'bg-primary/10 shadow-[inset_3px_0_0_0_var(--color-primary)]'
                      : pari
                        ? 'bg-card hover:bg-primary/[0.05]'
                        : 'bg-muted/[0.25] hover:bg-primary/[0.07]'}
                    ${disattivata ? 'opacity-50' : ''}
                  `}
                >
                  {colonne.map((col, i) => (
                    <td
                      key={col.key}
                      style={{ width: col.width, minWidth: col.width }}
                      className={`
                        px-4 ${padClass} ${ALLINEAMENTO[col.align] ?? ALLINEAMENTO.left}
                        ${i === 0 ? 'pl-5' : ''} ${i === colonne.length - 1 ? 'pr-5' : ''}
                      `}
                    >
                      {col.render ? col.render(riga) : (
                        <span className="text-[13px] font-bold text-foreground/80">
                          {riga[col.key] ?? '---'}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* spazio finale: l'ultima riga non resta mai attaccata al bordo / alla barra di scorrimento */}
        <div className="h-3" aria-hidden="true" />
      </div>

      {/* Footer: paginazione + conteggio (fuori dall'area scorrevole, non copre le righe) */}
      <div className="shrink-0 relative z-20 px-4 md:px-6 py-3 bg-muted/50 border-t border-border/50 flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => haPrec && onPagina(pagina - 1)}
            disabled={!haPrec || loading}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeft" size={12} />
            Prec
          </button>
          <span className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50 whitespace-nowrap">
            Pagina {pagina + 1} di {totalePagine}
          </span>
          <button
            type="button"
            onClick={() => haSucc && onPagina(pagina + 1)}
            disabled={!haSucc || loading}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Succ
            <Icon name="ChevronRight" size={12} />
          </button>
        </div>
        <span className="bg-primary/10 border border-primary/20 text-primary px-3 md:px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm whitespace-nowrap">
          {totale || 0} {totale === 1 ? 'elemento' : 'elementi'}
        </span>
      </div>
    </div>
  );
};

export default DataTable;
